import { useEffect, useRef, useState } from 'react'
import { apiGet } from '../../../generic/Api_Functions'
import useMosInfo from '../common/provider/useMosInfo'
import { Card, IconButton, LinearProgress, Stack, Tooltip, Typography } from '@mui/material'
import AssignmentIcon from '@mui/icons-material/Assignment'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { useSmartInterval } from '../../../generic/hooks/useSmartInterval'
import InspectionReportDialog from '../Inspection/InspectionReportDialog'

const TaskReferenceListWidget = ({ preview }) => {
  const [taskReferences, setTaskReferences] = useState(null)
  const [inspectionOpen, setInspectionOpen] = useState(false)
  const { t_ } = useSmartTranslation()
  const { fabricationOrder, jobReference, device, taskReference, setTaskReference, inspectionReportVersion } = useMosInfo()
  const lastInspectionReportVersionRef = useRef(inspectionReportVersion)

  // The inspection report needs the full MOS context to be submitted, not just the task.
  const canOpenInspection = Boolean(fabricationOrder?.id && jobReference?.id && device?.id)

  const openInspection = (task) => {
    setTaskReference(task)
    setInspectionOpen(true)
  }

  const restartTaskReferencesInterval = useSmartInterval(
    async () => {
      if (!jobReference) return null
      return await apiGet('mos/taskReference/byJobReference/' + jobReference.id)
    },
    1000,
    (data) => {
      setTaskReferences(data)
      if (data == null || data.length === 0) {
        if (taskReference != null) setTaskReference(null)
        return
      }
      if (!taskReference || !data.some(t => t.id === taskReference.id)) {
        setTaskReference(data[0])
      }
    }
  )

  // An inspection was reported (from this widget's dialog or from the Inspection widget): reload the
  // task progress right away instead of waiting for the next poll, so both progress bars move together.
  useEffect(() => {
    if (lastInspectionReportVersionRef.current === inspectionReportVersion) return
    lastInspectionReportVersionRef.current = inspectionReportVersion
    restartTaskReferencesInterval(true, true)
  }, [inspectionReportVersion, restartTaskReferencesInterval])

  const onClick = (task) => {
    setTaskReference(task)
  }

  return (
    <>
      <InspectionReportDialog open={inspectionOpen} onClose={() => setInspectionOpen(false)} />
      <Card
        elevation={0}
        sx={{
          height: '100%',
          p: 1,
          color: '#F3F3F3',
          backgroundColor: '#1F1F1F',
          borderRadius: 2
        }}
      >
        {!taskReferences
          ? (
            <Stack sx={{ height: '100%' }} direction='row' spacing={1} alignItems='center' justifyContent='center'>
              <Typography color='primary'>{t_('There is no active job')}</Typography>
            </Stack>
            )
          : (
            <Stack sx={{ height: '100%', minHeight: 0 }}>
              <Stack spacing={1.75} sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {taskReferences?.map((task, index) => {
                  const isSelected = taskReference?.id === task.id
                  const total = task.featuresQuantityTotal
                  const done = task.featureQuantityDone ?? 0
                  const hasProgress = total != null && total > 0
                  const isComplete = hasProgress && done >= total
                  const percentage = hasProgress ? Math.min(100, Math.round((done / total) * 100)) : 0

                  const stripeColor = (theme) => {
                    if (isSelected) return theme.palette.primary.main
                    if (isComplete) return theme.palette.success.main
                    return '#2E2E2E'
                  }

                  return (
                    <Stack
                      key={task.id}
                      direction='row'
                      alignItems='center'
                      spacing={1.25}
                      onClick={() => onClick(task)}
                      sx={{
                        px: 1,
                        py: 0.8,
                        borderRadius: 1.5,
                        borderStyle: 'solid',
                        borderWidth: '1px',
                        borderColor: isSelected ? 'primary.main' : '#2E2E2E',
                        borderLeftWidth: '4px',
                        borderLeftColor: stripeColor,
                        backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.12)' : '#232323',
                        cursor: 'pointer',
                        transition: 'box-shadow 0.2s ease, border-color 0.2s ease, background-color 0.2s ease',
                        boxShadow: theme => (isSelected ? `inset 0 0 0 1px ${theme.palette.primary.light}, 0 0 10px rgba(25, 118, 210, 0.28)` : 'none'),
                        '&:hover': {
                          borderColor: 'primary.main',
                          borderLeftColor: theme => (isComplete && !isSelected ? theme.palette.success.main : theme.palette.primary.main),
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                          boxShadow: theme => `inset 0 0 0 1px ${theme.palette.primary.light}, 0 0 8px rgba(25, 118, 210, 0.22)`
                        }
                      }}
                    >
                      <Stack
                        alignItems='center'
                        justifyContent='center'
                        sx={{
                          width: 30,
                          height: 30,
                          flexShrink: 0,
                          borderRadius: '50%',
                          border: theme => `2px solid ${isComplete ? theme.palette.success.main : theme.palette.warning.main}`,
                          backgroundColor: theme => isComplete ? theme.palette.success.main : 'transparent',
                          transition: 'background-color 0.2s ease, border-color 0.2s ease'
                        }}
                      >
                        <Typography
                          variant='caption'
                          sx={{
                            fontWeight: 700,
                            lineHeight: 1,
                            color: theme => isComplete ? theme.palette.common.black : theme.palette.warning.main
                          }}
                        >
                          {index + 1}
                        </Typography>
                      </Stack>
                      <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.5}>
                        <Stack direction='row' alignItems='center' spacing={1}>
                          <Typography
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {task.name}
                          </Typography>
                          {hasProgress && (
                            <Typography
                              variant='caption'
                              sx={{
                                color: isComplete ? 'success.main' : 'warning.main',
                                fontWeight: 700,
                                fontVariantNumeric: 'tabular-nums'
                              }}
                            >
                              {`${done}/${total}`}
                            </Typography>
                          )}
                        </Stack>
                        {hasProgress && (
                          <LinearProgress
                            variant='determinate'
                            value={percentage}
                            sx={{
                              height: 4,
                              borderRadius: 2,
                              backgroundColor: 'rgba(255, 255, 255, 0.08)',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: isComplete ? 'success.main' : 'warning.main',
                                borderRadius: 2
                              }
                            }}
                          />
                        )}
                      </Stack>
                      {hasProgress && (
                        <Tooltip title={t_('Inspection details')}>
                          <Stack component='span' sx={{ flexShrink: 0 }}>
                            <IconButton
                              aria-label={t_('Inspection details')}
                              disabled={!canOpenInspection}
                              onClick={(event) => {
                                event.stopPropagation()
                                openInspection(task)
                              }}
                              sx={{
                                // Touch-friendly target (44x44 minimum recommended size),
                                // styled like the Inspection widget open button so it reads as a button.
                                width: 44,
                                height: 44,
                                borderRadius: '12px',
                                border: theme => '1px solid ' + theme.palette.divider,
                                backgroundColor: theme => theme.palette.background.button,
                                color: '#F3F3F3',
                                '&:hover': { backgroundColor: theme => theme.palette.background.buttonHover },
                                '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.3)' }
                              }}
                            >
                              <AssignmentIcon />
                            </IconButton>
                          </Stack>
                        </Tooltip>
                      )}
                    </Stack>
                  )
                })}
              </Stack>
            </Stack>)}
      </Card>
    </>
  )
}

export default TaskReferenceListWidget
